import React, { useState, useEffect } from "react";

import Column from "./Column";
import Icon from "./Icon";
import Loading from "./Loading";
import PropTypes from "prop-types";
import Row from "./Row";
import { Text } from "./Texts";
import _find from "lodash/find";
import colors from "../styles/colors";
import styled from "styled-components";
import { Search, InfoCircle } from "styled-icons/bootstrap";
import { useTranslation } from "react-i18next";
import decodeHTMLEntities from "modules/utils/DecodeHTMLEntities";

const Dropdown = ({
  items = [],
  onSelected,
  minWidth,
  position,
  CustomDropdownButton,
  defaultIndex = 0,
  disabled,
  noBorder = false,
  selectedIndex = false,
  searchType,
  onSearch,
  searchLoading = false,
  searchDebounceMs = 500,
  hasMore = false,
}) => {
  const [isOptionsVisible, setOptionsVisible] = useState();
  const [selectedItem, setSelectedItem] = useState();
  let selectedTitle;
  const [searchInput, setSearchInput] = useState("");
  const [filteredItems, setFilteredItems] = useState(items);
  const [t] = useTranslation();
  const searchTimeoutRef = React.useRef(null);

  // Check if all items have "-" as title (but not empty array)
  let allHyphen = items.length > 0 && items.every((obj) => obj.title === "-");

  // Update filtered items when items change (from API or local filter)
  useEffect(() => {
    if (searchType && !allHyphen) {
      // If onSearch callback exists, items come from API - use them directly
      if (onSearch) {
        // Filter out items with title "-" for API search results
        const validItems = items.filter((item) => item.title !== "-");
        setFilteredItems(validItems);
      } else {
        // Local filter mode
        setFilteredItems(items.filter((item) => item.title.toLowerCase().includes(searchInput.toLowerCase())));
      }
    } else if (!searchType) {
      // Non-search mode - just use items as is
      setFilteredItems(items);
    }
  }, [items, allHyphen, searchType, onSearch]);

  // Local filter when searchInput changes (only for non-API search)
  useEffect(() => {
    if (searchType && !allHyphen && !onSearch) {
      setFilteredItems(items.filter((item) => item.title.toLowerCase().includes(searchInput.toLowerCase())));
    }
  }, [searchInput]);

  const handleSearchInput = (e) => {
    const value = e.target.value;
    setSearchInput(value);

    // If onSearch callback exists, debounce and call API
    if (onSearch) {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      searchTimeoutRef.current = setTimeout(() => {
        onSearch(value);
      }, searchDebounceMs);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  if (selectedItem && _find(items, { title: selectedItem.title })) {
    selectedTitle = selectedItem.title;
  }

  if (!selectedTitle && items && items[defaultIndex]) {
    selectedTitle = items[defaultIndex].title;
  } else if (!selectedTitle) {
    selectedTitle = "-";
  } else if (selectedIndex && selectedTitle != items[selectedIndex].title) {
    selectedTitle = items[selectedIndex].title;
  }

  if (typeof selectedTitle !== "object") {
    selectedTitle = <Text size="12px">{decodeHTMLEntities(selectedTitle)}</Text>;
  }

  const onDropdownButtonClicked = () => {
    if (!disabled) {
      setOptionsVisible(!isOptionsVisible);
    }
  };
  const onDropdownItemSelected = (item, index) => {
    onDropdownButtonClicked();
    setSelectedItem(item);
    onSelected(item, index);
  };

  if (typeof items === "object") {
    items = Object.values(items);
  }

  const viewItems = items.map((item, index) => (
    <DropdownItem key={index} item={item} index={index} onClick={onDropdownItemSelected}></DropdownItem>
  ));

  return (
    <Container>
      {CustomDropdownButton ? (
        <CustomDropdownButton onClick={onDropdownButtonClicked}></CustomDropdownButton>
      ) : (
        <DropdownButton
          className={`${noBorder === true ? "no-border" : ""} dropdown-button`}
          alignItems="center"
          columnSpacing="8px"
          minWidth={minWidth}
          onClick={onDropdownButtonClicked}
          disabled={disabled}
        >
          <Column width="100%">{selectedTitle}</Column>
          <Column wrap>
            <Icon name="icon-down-dir" size="14px"></Icon>
          </Column>
        </DropdownButton>
      )}
      <ItemsContainer isVisible={isOptionsVisible} position={position} minWidth={minWidth}>
        {searchType && !allHyphen ? (
          <>
            <SearchContent>
              <StyledInput 
                type="text" 
                className="input-box" 
                onChange={handleSearchInput}
                value={searchInput}
              />
              <SearchIcon color="#555" />
            </SearchContent>
            {searchLoading ? (
              <Loading />
            ) : filteredItems.length > 0 ? (
              <>
                <ScrollElement hasMarginTop>
                  {filteredItems.map((item, index) => (
                    <DropdownItem key={index} item={item} index={index} onClick={onDropdownItemSelected}></DropdownItem>
                  ))}
                </ScrollElement>
                {hasMore && (
                  <SearchHintContainer>
                    <InfoIcon />
                    <span>{t("__fbads_ads_account_search_hint")}</span>
                  </SearchHintContainer>
                )}
              </>
            ) : (
              <StyledItem style={{ marginTop: '8px' }}>
                <div className="account-not-found">{t("__fbads_ads_account_not_found")}</div>
              </StyledItem>
            )}
          </>
        ) : (
          <ScrollElement>
            {viewItems}
          </ScrollElement>
        )}
      </ItemsContainer>
      {isOptionsVisible && <Backdrop onClick={() => setOptionsVisible(false)} />}
    </Container>
  );
};

Dropdown.propTypes = {
  item: PropTypes.array,
  onSelected: PropTypes.func,
  minWidth: PropTypes.string,
  position: PropTypes.object,
  CustomDropdownButton: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  defaultIndex: PropTypes.number,
};

export default Dropdown;

const DropdownItem = ({ item, index, onClick }) => {
  const accountId = item.account_id || item.ads_account_id;
  return (
    <StyledItem onClick={() => onClick(item, index)}>
      {typeof item.title === "object" ? (
        decodeHTMLEntities(item.title)
      ) : (
        <div className="dropdown-item-content">
          <Text size="12px">{decodeHTMLEntities(item.title)}</Text>
          {accountId && (
            <Text size="10px" className="account-id">{accountId}</Text>
          )}
        </div>
      )}
    </StyledItem>
  );
};

const Container = styled.div`
  position: relative;

  > *:first-child {
    cursor: pointer;
  }

  .custom-dropdown-button {
    display: flex;
    align-items: center;

    > * {
      min-width: 0;
    }
  }
`;

const DropdownButton = styled(Row).attrs((props) => ({
  minWidth: props.minWidth,
  opacity: props.disabled ? 0.75 : 1,
}))`
  border: 1px solid #f0f0f0;
  padding: 4px 8px 4px 16px;
  border-radius: 8px;
  box-sizing: border-box;
  min-width: ${(props) => props.minWidth};
  opacity: ${(props) => props.opacity};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")} !important;
  background-color: #fff;

  &.no-border {
    border: none;
    background-color: transparent;
  }

  &.no-border .column > p {
    font-size: 1.2rem;
  }
`;

const ItemsContainer = styled.div.attrs((props) => ({
  display: props.isVisible ? "block" : "none",
  top: props.position ? props.position.top : "35px",
  right: props.position && props.position.right ? props.position.right : null,
  minWidth: props.minWidth,
}))`
  display: ${(props) => props.display};
  position: absolute;
  border: 1px solid #f0f0f0;
  background-color: ${colors.backgroundSection};
  box-shadow: ${colors.sectionShadow} 0 10px 30px;
  min-width: 150px;
  border-radius: 8px;
  z-index: 999;
  top: ${(props) => props.top};
  right: ${(props) => props.right};
  min-width: ${(props) => props.minWidth};
  padding: 16px 0;
  text-align: left;
  max-width: 100%;

  @media screen and (max-width: 834px) {
    box-shadow: none;
    width: calc(100% - 32px);
    position: fixed;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    margin: 16px;
    text-align: center;
    max-height: calc(100vh - 150px);
    min-width: unset;

    p {
      font-size: 14px;
    }
  }
`;

const ScrollElement = styled.div`
  max-height: 170px;
  overflow: auto;
  margin-top: ${(props) => (props.hasMarginTop ? "8px" : "0")};
`;

const StyledItem = styled.div`
  margin-left: 8px;
  margin-right: 8px;
  padding: 8px 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f5f5f5;
  }

  .dropdown-item-content {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .account-id {
    color: #888;
  }

  .page-not-found {
    font-size: 12px;
    color: #555;
    line-height: 1rem;
  }

  .account-not-found {
    font-weight: 400;
    font-size: 11px;
    line-height: 18px;
    letter-spacing: 0;
    color: #555;
  }

  @media screen and (max-width: 834px) {
    padding: 16px;
  }
`;

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  z-index: 998;

  @media screen and (max-width: 834px) {
    background-color: rgba(0, 0, 0, 0.5);
  }
`;

const SearchIcon = styled(Search).attrs((props) => ({
  color: props.color,
}))`
  color: ${(props) => props.color};
  width: 16px;
  height: 16px;
  position: absolute;
  left: 20px;
  top: 50%;
  transform: translateY(-50%);
`;

const InfoIcon = styled(InfoCircle)`
  width: 14px;
  height: 14px;
  color: #888;
  flex-shrink: 0;
`;

const SearchHintContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 16px;
  margin: 8px 10px 0 10px;
  background-color: rgba(229, 240, 255, 1);
  border-radius: 6px;
  font-size: 11px;
  line-height: 16px;
  color: #666;
`;

const SearchContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  line-height: 1.5rem;
  font-size: 12px;
  padding: 0 10px;
  position: relative;
  box-sizing: border-box;
`;

const StyledInput = styled.input`
  padding: 6px 12px 6px 35px;
  border: 1px solid #ddd;
  border-radius: 8px;
  width: 100%;
  box-sizing: border-box;

  &::placeholder {
    color: #aaa;
  }
`;
